import React, { useState, useEffect, useCallback } from 'react';
import { Users, Loader2, AlertTriangle, CalendarDays, TrendingUp } from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';

/**
 * RetentionTab — Week-over-week and 30-day retention analysis.
 *
 * Props:
 *   filters: { district_id, campus_id }
 */

const RetentionTab = ({ filters = {} }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const res = await adminAnalytics.getRetention(params);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load retention data.');
        } finally {
            setLoading(false);
        }
    }, [filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading retention…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
            </div>
        );
    }

    const live = data?.live_metrics || {};
    const cohorts = data?.cohorts || [];
    const wowRate = live.week_over_week_return_rate_pct ?? 0;
    const d30Rate = live.thirty_day_retention_rate_pct ?? 0;

    const wowColor = wowRate >= 70 ? '#10b981' : wowRate >= 40 ? '#f59e0b' : '#ef4444';
    const d30Color = d30Rate >= 50 ? '#10b981' : d30Rate >= 25 ? '#f59e0b' : '#ef4444';

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Retention</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    User return rates and cohort analysis
                </p>
            </div>

            {/* Live metrics */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 12, marginBottom: 28,
            }}>
                <MetricCard
                    label="Week-over-Week Return" value={wowRate}
                    icon={TrendingUp} accent={wowColor} format="percent"
                    sub="Users active in both current and previous 7-day window"
                />
                <MetricCard
                    label="30-Day Retention" value={d30Rate}
                    icon={CalendarDays} accent={d30Color} format="percent"
                    sub="Users active now who were also active 23-30 days ago"
                />
            </div>

            {/* Cohort table */}
            <div>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12 }}>
                    Weekly Cohorts
                </h3>

                {cohorts.length === 0 ? (
                    <div style={{
                        background: '#f8fafc', borderRadius: 12, padding: '32px 0',
                        textAlign: 'center', border: '1px solid #f1f5f9',
                    }}>
                        <Users size={28} style={{ color: '#94a3b8', margin: '0 auto 8px' }} />
                        <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                            Cohort data will appear when retention_cohorts_weekly is populated.
                        </p>
                        <p style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>
                            Live metrics above are computed from user_activity_daily.
                        </p>
                    </div>
                ) : (
                    <div style={{
                        background: '#fff', border: '1px solid #e5e7eb',
                        borderRadius: 12, overflow: 'auto',
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                        Cohort Week
                                    </th>
                                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                        Users
                                    </th>
                                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                        Retained
                                    </th>
                                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                        Rate
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {cohorts.map((c, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 14px', color: '#374151' }}>
                                            {c.cohort_week_start}
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                            {c.cohort_size || '—'}
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                            {c.retained_count || '—'}
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                            <span style={{
                                                fontWeight: 600,
                                                color: (c.retention_rate_pct || 0) >= 50 ? '#10b981' : '#f59e0b',
                                            }}>
                                                {c.retention_rate_pct != null ? `${c.retention_rate_pct}%` : '—'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {data?.note && (
                <p style={{
                    fontSize: 11, color: '#94a3b8', marginTop: 16,
                    borderTop: '1px solid #f1f5f9', paddingTop: 12,
                }}>
                    {data.note}
                </p>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default RetentionTab;
